/**
 * Overlays, ported from the prototype's shell: popovers, menus, dialogs,
 * toasts and tooltips, all rendered in one portal at the root (never
 * inside a glass element, whose backdrop-filter would trap fixed children).
 * Escape closes the top one; focus returns to the opener; outside clicks
 * close the top non-modal popover; placement is logical (start/end mirror
 * in RTL).
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { tr, useT } from '../i18n';
import { pushBack } from '@neosocial/ui/back';
import { readLayout } from '@neosocial/ui/layout';
import { Btn, Icon, Range, type IconName } from './kit';

/**
 * Menus are action sheets (48px rows from the bottom, over a scrim) on a
 * phone, and on a tablet with a touch screen (§12); anchored menus elsewhere.
 */
export function menusAsSheets(): boolean {
  const l = readLayout();
  return l.layout === 'phone' || (l.layout === 'tablet' && l.pointer === 'coarse');
}

/** Phones and tablets: Back closes the layer (the back stack); the desktop has Escape. */
const backStackOn = (): boolean => readLayout().layout !== 'desktop';

/**
 * A tap on a menu sheet's scrim closes the menu on pointerdown; the click
 * that follows must not reach what was under the scrim.
 */
function swallowNextClick(): void {
  const eat = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    off();
  };
  const off = () => {
    clearTimeout(t);
    document.removeEventListener('click', eat, true);
  };
  const t = setTimeout(off, 800);
  document.addEventListener('click', eat, true);
}

/* ---------- placement (prototype NX.ui.place) ---------- */

export type Anchor = Element | { x: number; y: number } | null;
export type Placement =
  `${'top' | 'bottom' | 'start' | 'end'}${'' | '-start' | '-center' | '-end'}`;

interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Where each anchor element was last seen on the page. An anchor can leave
 * the page while its pop-up is open (a voice tile's More button when the
 * person leaves voice): a detached element's rect is all zeros, so the
 * menu, placed again when its items changed, jumped to the top-left corner
 * (QA 2026-10-28 round 4). A detached anchor keeps its last place instead.
 */
const lastRects = new WeakMap<Element, Rect>();

/**
 * Records where `el` is now, so a pop-up opened on it later still opens
 * there if it has left the page by then (`anchorRect`). For an element a
 * menu item anchors to (a row or tile), taken when the menu opens.
 */
export function rememberAnchor(el: Element): void {
  if (el.isConnected) anchorRect(el);
}

/**
 * The anchor's rect: an element on the page as it is now (and remembered),
 * a detached one where it was last seen, a point as a zero-size rect, and
 * no anchor (or an element never seen on the page) the window's centre.
 */
export function anchorRect(anchor: Anchor): Rect {
  if (anchor && 'getBoundingClientRect' in anchor) {
    if (anchor.isConnected) {
      const b = anchor.getBoundingClientRect();
      const r = {
        left: b.left,
        right: b.right,
        top: b.top,
        bottom: b.bottom,
        width: b.width,
        height: b.height,
      };
      lastRects.set(anchor, r);
      return r;
    }
    const last = lastRects.get(anchor);
    if (last) return last;
  } else if (anchor && 'x' in anchor)
    return {
      left: anchor.x,
      right: anchor.x,
      top: anchor.y,
      bottom: anchor.y,
      width: 0,
      height: 0,
    };
  return {
    left: innerWidth / 2,
    right: innerWidth / 2,
    top: innerHeight / 2,
    bottom: innerHeight / 2,
    width: 0,
    height: 0,
  };
}

export function place(
  el: HTMLElement,
  anchor: Anchor,
  placement: Placement = 'bottom-start',
  offset = 8,
): void {
  const r = anchorRect(anchor);
  const parts = placement.split('-');
  let side = parts[0] as string;
  const align = parts[1] ?? 'center';
  if (side === 'start') side = 'left';
  else if (side === 'end') side = 'right';
  el.style.left = '0px';
  el.style.top = '0px';
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const m = 8;
  const room: Record<string, number> = {
    top: r.top - offset - m,
    bottom: innerHeight - r.bottom - offset - m,
    left: r.left - offset - m,
    right: innerWidth - r.right - offset - m,
  };
  const opp: Record<string, string> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };
  const need = side === 'top' || side === 'bottom' ? h : w;
  if ((room[side] ?? 0) < need && (room[opp[side]!] ?? 0) > (room[side] ?? 0)) side = opp[side]!;
  let x: number;
  let y: number;
  if (side === 'top' || side === 'bottom') {
    y = side === 'top' ? r.top - h - offset : r.bottom + offset;
    if (align === 'center') x = r.left + r.width / 2 - w / 2;
    else if (align === 'start') x = r.left;
    else x = r.right - w;
  } else {
    x = side === 'left' ? r.left - w - offset : r.right + offset;
    if (align === 'center') y = r.top + r.height / 2 - h / 2;
    else if (align === 'start') y = r.top;
    else y = r.bottom - h;
  }
  const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
  x = clamp(x, m, Math.max(m, innerWidth - w - m));
  y = clamp(y, m, Math.max(m, innerHeight - h - m));
  el.style.left = Math.round(x) + 'px';
  el.style.top = Math.round(y) + 'px';
  el.dataset.side = side;
  const ox =
    side === 'left'
      ? '100%'
      : side === 'right'
        ? '0%'
        : clamp(r.left + r.width / 2 - x, 0, w) + 'px';
  const oy =
    side === 'top'
      ? '100%'
      : side === 'bottom'
        ? '0%'
        : clamp(r.top + r.height / 2 - y, 0, h) + 'px';
  el.style.transformOrigin = `${ox} ${oy}`;
}

/* ---------- input modality and exits ---------- */

/**
 * How the last thing was opened: a menu opened by the pointer has no active
 * item until ArrowDown; one opened by the keyboard (Enter, Shift+F10, the
 * context-menu key) activates its first item (§7.15). A script's `.click()`
 * fires no pointer event, so it counts as the keyboard.
 */
let modality: 'pointer' | 'keyboard' = 'keyboard';
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', () => (modality = 'pointer'), true);
  document.addEventListener('keydown', () => (modality = 'keyboard'), true);
}

/** Whether exit animations can be watched (a real browser; jsdom closes at once). */
export const animatesExits = (): boolean =>
  typeof Element !== 'undefined' && typeof Element.prototype.getAnimations === 'function';

/** Calls `done` once `el`'s exit animation ends, or after `ms` at the latest. */
function afterExit(el: HTMLElement, done: () => void, ms = 150): void {
  let over = false;
  const end = () => {
    if (over) return;
    over = true;
    clearTimeout(t);
    done();
  };
  const t = setTimeout(end, ms);
  requestAnimationFrame(() => {
    const run = el.getAnimations?.() ?? [];
    if (!run.length) return end();
    void Promise.all(run.map((a) => a.finished.catch(() => undefined))).then(end);
  });
}

/** Marks the one active item of a menu (pointer and keyboard share it). */
function activate(box: HTMLElement | null, el: HTMLElement | null, focus = true): void {
  if (!box) return;
  for (const x of box.querySelectorAll('[data-active]'))
    if (x !== el) x.removeAttribute('data-active');
  if (!el) return;
  el.setAttribute('data-active', '');
  if (focus && document.activeElement !== el) el.focus({ preventScroll: true });
  el.scrollIntoView?.({ block: 'nearest' });
  // a cut label shows in full as the item's title (§7.0 rule 4)
  const l = el.querySelector<HTMLElement>('.mi-l');
  if (l && !el.title && l.scrollWidth > l.clientWidth) el.title = l.textContent ?? '';
}

/* ---------- the Escape stack ---------- */

const escStack: Array<() => void> = [];
export function pushEscape(fn: () => void): () => void {
  escStack.push(fn);
  return () => {
    const i = escStack.lastIndexOf(fn);
    if (i >= 0) escStack.splice(i, 1);
  };
}
export function overlayOpen(): boolean {
  return escStack.length > 0;
}
if (typeof document !== 'undefined') {
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape' || !escStack.length) return;
      e.preventDefault();
      e.stopPropagation();
      escStack[escStack.length - 1]?.();
    },
    true,
  );
}

export function portalRoot(): HTMLElement {
  let el = document.getElementById('nx-portal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'nx-portal';
    document.body.appendChild(el);
  }
  return el;
}

function focusables(el: HTMLElement): HTMLElement[] {
  return Array.from(
    el.querySelectorAll<HTMLElement>(
      'button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]), textarea:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((x) => x.offsetParent !== null || x === document.activeElement);
}

/* ---------- popovers and menus (imperative) ---------- */

export interface MenuItem {
  id?: string;
  label?: string;
  sub?: string;
  icon?: IconName;
  iconNode?: ReactNode;
  kbd?: string;
  danger?: boolean;
  disabled?: boolean;
  checked?: boolean;
  /** With `checked`: an on/off item (menuitemcheckbox), not one of a set. */
  toggle?: boolean;
  sep?: boolean;
  header?: string;
  run?: () => void;
  /**
   * A submenu beside the item (Discord's "Roles ›"), built when it opens:
   * it opens on hover, a click, Enter or Space, or the arrow key toward it,
   * and its items may be live.
   */
  submenu?: () => MenuItem[] | LiveMenuItems;
  /** A click runs it and leaves its menu open (a submenu's checkboxes, as Discord's role list). */
  keepOpen?: boolean;
  /**
   * A slider item (Discord's "User Volume"): the label and the value over a
   * kit slider. ←/→ change it while the item is active, the pointer drags
   * it, and it never closes the menu.
   */
  range?: MenuRange;
}

export interface MenuRange {
  value: number;
  min: number;
  max: number;
  step: number;
  /** The value as it is shown and read out ("150%"). */
  format: (v: number) => string;
  onChange: (v: number) => void;
}

/** A menu's slider item (`MenuItem.range`); `leaving`: drawn the same, inert. */
function RangeItem({
  it,
  range,
  leaving,
  onEnter,
}: {
  it: MenuItem;
  range: MenuRange;
  leaving?: boolean;
  onEnter?: () => void;
}) {
  const { value, min, max, step, format, onChange } = range;
  const text = format(value);
  const head = (
    <span className="mi-range-h">
      <span className="mi-l">{it.label}</span>
      <span className="nx-range-v nx-num" aria-live={leaving ? undefined : 'polite'}>
        {text}
      </span>
    </span>
  );
  if (leaving)
    return (
      <div className="mi mi-range">
        {head}
        <Range value={value} min={min} max={max} step={step} readOnly tabIndex={-1} />
      </div>
    );
  const set = (v: number) => onChange(Math.max(min, Math.min(max, v)));
  return (
    <div
      role="menuitem"
      tabIndex={-1}
      data-id={it.id}
      className="mi mi-range"
      aria-label={it.label}
      onPointerEnter={onEnter}
      onPointerMove={(e) => {
        if (!e.currentTarget.hasAttribute('data-active'))
          activate(e.currentTarget.closest('.nx-menu-in'), e.currentTarget);
      }}
      onKeyDown={(e) => {
        const self = e.target === e.currentTarget;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          // the slider's own keys (never "back" out of a submenu)
          e.stopPropagation();
          if (!self) return; // the focused slider moves itself
          e.preventDefault();
          const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
          set(value + ((e.key === 'ArrowRight') !== rtl ? step : -step));
        } else if (!self && /^(ArrowUp|ArrowDown|Home|End)$/.test(e.key)) {
          // from the dragged slider: the menu moves on from this item
          e.currentTarget.focus({ preventScroll: true });
        }
      }}
    >
      {head}
      <Range
        value={value}
        min={min}
        max={max}
        step={step}
        tabIndex={-1}
        aria-label={it.label}
        aria-valuetext={text}
        onChange={(e) => set(Number(e.currentTarget.value))}
        onPointerUp={(e) =>
          e.currentTarget.closest<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true })
        }
      />
    </div>
  );
}

interface PopItem {
  id: number;
  anchor: Anchor;
  placement: Placement;
  offset: number;
  cls: string;
  role: string;
  label?: string;
  plain?: boolean;
  focus: boolean;
  focusSel?: string;
  restore: HTMLElement | null;
  render: (close: () => void, id: number, leaving?: boolean) => ReactNode;
  /** Over an action sheet's rows: whose menu it is (a member's picture and name). */
  head?: ReactNode;
  onClose?: () => void;
  menu?: MenuItem[];
  width?: number;
  /** A submenu's menu: it closes with that one, and an outside click closes both. */
  parent?: number;
}

interface OverlayStore {
  pops: PopItem[];
  toasts: ToastItem[];
  /** Closed pop-ups still playing their exit (not open: no keys, no clicks). */
  leaving?: PopItem[];
}

export const useOverlays = create<OverlayStore>(() => ({ pops: [], toasts: [], leaving: [] }));

let seq = 0;

export interface PopoverOptions {
  placement?: Placement;
  offset?: number;
  cls?: string;
  role?: string;
  label?: string;
  plain?: boolean;
  focus?: boolean;
  focusSel?: string;
  onClose?: () => void;
  width?: number;
  /**
   * Opened by an item of a menu on this same anchor (the voice tile's
   * "more" menu, then Volume): the menu that just closed is not a click on
   * the opener, so the pop-up opens.
   */
  fromMenu?: boolean;
  /** A submenu: the pop-up of the menu it opens from (see `PopItem.parent`). */
  parent?: number;
  /** A menu's head while it is an action sheet (see `PopItem.head`). */
  head?: ReactNode;
}

// a click on the opener closes its popover instead of opening it again
let lastClosed: { anchor: Anchor; at: number } = { anchor: null, at: 0 };

/**
 * Closes a popover (its submenus first). `byOpener`: the pointer went down
 * on the popover's own opener, so the click that follows must not open it
 * again. Any other close (Escape, a menu item, an outside click) leaves the
 * opener working: the dock's "View profile" opens the card from the button
 * its menu came from.
 */
export function closePopover(id: number, byOpener = false): void {
  if (!useOverlays.getState().pops.some((p) => p.id === id)) return;
  // its submenus first
  for (const c of useOverlays.getState().pops.filter((p) => p.parent === id)) closePopover(c.id);
  const st = useOverlays.getState();
  const it = st.pops.find((p) => p.id === id);
  if (!it) return;
  lastClosed = byOpener ? { anchor: it.anchor, at: Date.now() } : { anchor: null, at: 0 };
  // it plays its exit while no longer open (§7.15, I13)
  useOverlays.setState({
    pops: st.pops.filter((p) => p.id !== id),
    leaving: animatesExits() ? [...(st.leaving ?? []), it] : (st.leaving ?? []),
  });
  it.onClose?.();
  if (it.restore && document.contains(it.restore)) it.restore.focus({ preventScroll: true });
}

export function closeAllPopovers(): void {
  for (const p of useOverlays.getState().pops.slice().reverse()) closePopover(p.id);
}

export function openPopover(
  anchor: Anchor,
  render: (close: () => void, id: number, leaving?: boolean) => ReactNode,
  o: PopoverOptions = {},
): () => void {
  if (!o.fromMenu && anchor && anchor === lastClosed.anchor && Date.now() - lastClosed.at < 300)
    return () => {};
  const id = ++seq;
  const item: PopItem = {
    id,
    anchor,
    placement: o.placement ?? 'bottom-start',
    offset: o.offset ?? 8,
    cls: o.cls ?? '',
    role: o.role ?? 'dialog',
    label: o.label,
    plain: o.plain,
    focus: o.focus !== false,
    focusSel: o.focusSel,
    restore: document.activeElement as HTMLElement | null,
    render,
    onClose: o.onClose,
    width: o.width,
    parent: o.parent,
    head: o.head,
  };
  useOverlays.setState((s) => ({ pops: [...s.pops, item] }));
  return () => closePopover(id);
}

/**
 * A menu's items that follow live state, for a menu that stays open while
 * things change (React's useSyncExternalStore contract): `get` returns the
 * same array until something it reads changed; `subscribe` says when to
 * ask again. QA 2026-10-28 round 2: an open member menu kept "Server mute"
 * after another moderator muted the person.
 */
export interface LiveMenuItems {
  subscribe(onChange: () => void): () => void;
  get(): MenuItem[];
}

interface MenuOptions {
  placement?: Placement;
  label?: string;
  offset?: number;
  /** A submenu (`MenuItem.submenu`): the pop-up of the menu it opens from. */
  parent?: number;
  /** Move the focus into the menu (default); a submenu opened by hover leaves it. */
  focus?: boolean;
  cls?: string;
  onClose?: () => void;
  /** The pointer came into the menu (a submenu tells its menu to keep it open). */
  onEnter?: () => void;
  /**
   * On a phone or a touch tablet (an action sheet, which covers what it was
   * opened on), a head over the rows that says whose menu it is: a member's
   * picture and name, as Discord's mobile user sheet (QA V-11).
   */
  sheetHead?: ReactNode;
}

export function openMenu(
  anchor: Anchor,
  items: MenuItem[] | LiveMenuItems,
  o: MenuOptions = {},
): () => void {
  const at = { parent: o.parent, onEnter: o.onEnter };
  const body = Array.isArray(items)
    ? (close: () => void, id: number, leaving?: boolean) => (
        <MenuBody items={items} close={close} popId={id} leaving={leaving} {...at} />
      )
    : (close: () => void, id: number, leaving?: boolean) => (
        <LiveMenuBody src={items} close={close} popId={id} leaving={leaving} {...at} />
      );
  return openPopover(anchor, body, {
    placement: o.placement ?? 'bottom-start',
    cls: 'nx-menu' + (o.cls ? ' ' + o.cls : ''),
    role: 'menu',
    label: o.label,
    offset: o.offset,
    focus: o.focus,
    onClose: o.onClose,
    parent: o.parent,
    // a submenu opens on its menu's item, which a click there may just have closed
    fromMenu: o.parent !== undefined,
    focusSel: '[role^="menuitem"]:not([aria-disabled="true"])',
    head: o.sheetHead,
  });
}

interface MenuBodyProps {
  close: () => void;
  popId: number;
  parent?: number;
  onEnter?: () => void;
  /** Playing its exit: drawn the same, but no roles, ids or handlers. */
  leaving?: boolean;
}

/** The menu rebuilt whenever its items change while it is open. */
function LiveMenuBody({ src, ...rest }: MenuBodyProps & { src: LiveMenuItems }) {
  const items = useSyncExternalStore(src.subscribe, src.get);
  return <MenuBody items={items} {...rest} />;
}

/** How long the pointer may cross other items on its way into an open submenu. */
const SUBMENU_GRACE_MS = 300;

function MenuBody({
  items,
  close,
  popId,
  parent,
  onEnter,
  leaving,
}: MenuBodyProps & { items: MenuItem[] }) {
  // the open submenu: which item, and how to close it
  const sub = useRef<{ at: number; close: () => void } | null>(null);
  const [subAt, setSubAt] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  // an item's click closes the whole menu, a submenu's with the menu it is in
  const closeAll = () => (parent !== undefined ? closePopover(parent) : close());
  const closeSub = () => {
    clearTimeout(timer.current);
    const s = sub.current;
    sub.current = null;
    s?.close();
  };
  // a live menu that no longer has the item its open submenu came from closes it
  useEffect(() => {
    const s = sub.current;
    if (s && !items[s.at]?.submenu) {
      sub.current = null;
      s.close();
    }
  }, [items]);
  const openSub = (i: number, el: HTMLElement, focus: boolean) => {
    clearTimeout(timer.current);
    const it = items[i];
    if (!it?.submenu || it.disabled) return;
    // already open by hover: the keyboard reopens it with the focus inside
    if (sub.current?.at === i && !focus) return;
    closeSub();
    const off = openMenu(el, it.submenu(), {
      placement: 'end-start',
      offset: 10,
      parent: popId,
      focus,
      cls: 'ns-submenu',
      label: it.label,
      onEnter: () => clearTimeout(timer.current),
      onClose: () => {
        if (sub.current?.at === i) sub.current = null;
        setSubAt((cur) => (cur === i ? null : cur));
      },
    });
    sub.current = { at: i, close: off };
    setSubAt(i);
  };
  // the pointer on another item: the submenu closes unless it gets there first
  const leaveSub = () => {
    if (!sub.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(closeSub, SUBMENU_GRACE_MS);
  };
  const forward = 'ArrowRight';
  const back = 'ArrowLeft';
  if (leaving) {
    // the same look while it fades out; nothing in it can be found or used
    return (
      <div className="nx-menu-in">
        {items.map((it, i) =>
          it.sep ? (
            <hr key={i} />
          ) : it.header ? (
            <div key={i} className="mi-h">
              {it.header}
            </div>
          ) : it.range ? (
            <RangeItem key={i} it={it} range={it.range} leaving />
          ) : (
            <div key={i} className={'mi' + (it.danger ? ' danger' : '')}>
              {it.iconNode ?? (it.icon ? <Icon name={it.icon} /> : <span className="mi-gap" />)}
              <span className="mi-t">
                <span className="mi-l">{it.label}</span>
                {it.sub ? <span className="mi-s">{it.sub}</span> : null}
              </span>
              {it.kbd ? <kbd className="nx-kbd">{it.kbd}</kbd> : null}
              {it.checked ? <Icon name="check" cls="mi-chk" /> : null}
              {it.submenu ? <Icon name="chev-right" cls="mi-more" /> : null}
            </div>
          ),
        )}
      </div>
    );
  }
  return (
    <div
      className="nx-menu-in"
      tabIndex={-1}
      onPointerEnter={onEnter}
      onPointerLeave={(e) => {
        // the highlight follows the pointer out; an open submenu keeps its item
        activate(e.currentTarget, null);
      }}
      onKeyDown={(e) => {
        const box = e.currentTarget as HTMLElement;
        const list = Array.from(
          box.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])'),
        );
        const active = document.activeElement as HTMLElement | null;
        const i = list.indexOf(active as HTMLElement);
        const n = list.length;
        if (e.key === forward && active?.dataset.sub !== undefined) {
          e.preventDefault();
          openSub(Number(active.dataset.sub), active, true);
        } else if (e.key === back && parent !== undefined) {
          // back to the item it opened from (the focus returns there)
          e.preventDefault();
          close();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          activate(box, list[(i + 1) % n] ?? null);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          activate(box, list[i < 0 ? n - 1 : (i - 1 + n) % n] ?? null);
        } else if (e.key === 'Home') {
          e.preventDefault();
          activate(box, list[0] ?? null);
        } else if (e.key === 'End') {
          e.preventDefault();
          activate(box, list[n - 1] ?? null);
        } else if (e.key === 'Tab') {
          e.preventDefault();
          closeAll();
        }
      }}
    >
      {items.map((it, i) => {
        if (it.sep) return <hr key={i} role="separator" />;
        if (it.header)
          return (
            <div key={i} className="mi-h">
              {it.header}
            </div>
          );
        if (it.range) return <RangeItem key={i} it={it} range={it.range} onEnter={leaveSub} />;
        const hasSub = !!it.submenu;
        return (
          <button
            key={i}
            type="button"
            role={
              it.checked !== undefined
                ? it.toggle
                  ? 'menuitemcheckbox'
                  : 'menuitemradio'
                : 'menuitem'
            }
            data-id={it.id}
            data-sub={hasSub ? i : undefined}
            className={'mi' + (it.danger ? ' danger' : '')}
            aria-disabled={it.disabled || undefined}
            aria-checked={it.checked}
            aria-haspopup={hasSub ? 'menu' : undefined}
            aria-expanded={hasSub ? subAt === i : undefined}
            tabIndex={-1}
            onPointerEnter={(e) => (hasSub ? openSub(i, e.currentTarget, false) : leaveSub())}
            onPointerMove={(e) => {
              if (!e.currentTarget.hasAttribute('data-active'))
                activate(
                  e.currentTarget.closest('.nx-menu-in'),
                  it.disabled ? null : e.currentTarget,
                );
            }}
            onClick={(e) => {
              if (it.disabled) return;
              // a submenu's item opens it (from the keyboard with the focus inside)
              if (hasSub) return openSub(i, e.currentTarget, e.detail === 0);
              if (!it.keepOpen) closeAll();
              // Closed by choosing an item, not by a click on its opener: what the
              // item opens may use the same anchor ("View profile" from the dock).
              lastClosed = { anchor: null, at: 0 };
              it.run?.();
            }}
          >
            {it.iconNode ?? (it.icon ? <Icon name={it.icon} /> : <span className="mi-gap" />)}
            <span className="mi-t">
              <span className="mi-l">{it.label}</span>
              {it.sub ? <span className="mi-s">{it.sub}</span> : null}
            </span>
            {it.kbd ? <kbd className="nx-kbd">{it.kbd}</kbd> : null}
            {it.checked ? <Icon name="check" cls="mi-chk" /> : null}
            {hasSub ? <Icon name="chev-right" cls="mi-more" /> : null}
          </button>
        );
      })}
    </div>
  );
}

function PopLayer({ item, leaving }: { item: PopItem; leaving?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  // a menu on a phone or a touch tablet is an action sheet, for its whole life
  const [sheet] = useState(() => item.role === 'menu' && menusAsSheets());
  const close = () => closePopover(item.id);
  // the exit plays, then the node goes
  useEffect(() => {
    const el = ref.current;
    if (!leaving || !el) return;
    afterExit(el, () =>
      useOverlays.setState((s) => ({ leaving: (s.leaving ?? []).filter((p) => p.id !== item.id) })),
    );
  }, [leaving, item.id]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || sheet) return;
    if (item.width) el.style.width = item.width + 'px';
    place(el, item.anchor, item.placement, item.offset);
    const onResize = () => place(el, item.anchor, item.placement, item.offset);
    window.addEventListener('resize', onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => {
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, [item, sheet]);
  useEffect(() => {
    if (leaving) return;
    const off = pushEscape(() => closePopover(item.id));
    // Back closes it on phones and tablets (§12.4 rule 7): an action sheet,
    // and an anchored pop-up too. QA-02: a tablet's profile card is a
    // popover; Back closed the member drawer under it, then left the app.
    const offBack = backStackOn() ? pushBack(() => closePopover(item.id)) : null;
    // how it was opened, read now (the opening event has just run)
    const byKeyboard = modality === 'keyboard';
    if (item.focus) {
      requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        // its content already took the focus as it mounted (the profile's
        // note after "Add note"): that focus stays (V-01)
        if (el.contains(document.activeElement) && document.activeElement !== el) return;
        if (item.role === 'menu') {
          // by keyboard the first item is active; by pointer none is, until ArrowDown
          const box = el.querySelector<HTMLElement>('.nx-menu-in');
          const first = item.focusSel ? el.querySelector<HTMLElement>(item.focusSel) : null;
          if (byKeyboard && first) activate(box, first);
          else (box ?? el).focus({ preventScroll: true });
          return;
        }
        const f =
          (item.focusSel ? el.querySelector<HTMLElement>(item.focusSel) : null) ??
          focusables(el)[0];
        f?.focus({ preventScroll: true });
      });
    }
    return () => {
      off();
      offBack?.();
    };
  }, [item, leaving, sheet]);
  const cls = (item.cls ? ' ' + item.cls : '').replace(
    / nx-menu(?=\s|$)/,
    leaving ? ' nx-menu-out' : ' nx-menu',
  );
  const pop = (
    <div
      ref={ref}
      className={
        'nx-pop' + (item.plain ? ' nx-solidpop' : ' nx-raised') + cls + (sheet ? ' is-sheet' : '')
      }
      role={leaving ? undefined : item.role}
      aria-label={leaving ? undefined : item.label}
      aria-hidden={leaving || undefined}
      inert={leaving || undefined}
      data-state={leaving ? 'closed' : 'open'}
      data-pop={leaving ? undefined : item.id}
    >
      {sheet && item.head ? <div className="nx-sheet-who">{item.head}</div> : null}
      {item.render(close, item.id, leaving)}
    </div>
  );
  if (!sheet) return pop;
  return (
    <>
      <div className="nx-menu-scrim" data-state={leaving ? 'closed' : 'open'} aria-hidden="true" />
      {pop}
    </>
  );
}

/* ---------- toasts (§7.20) ---------- */

export interface ToastItem {
  id: number;
  text: string;
  icon?: IconName;
  tone?: 'ok' | 'warn' | 'danger' | 'mode';
  action?: { label: string; run: () => void };
  duration?: number;
  closing?: boolean;
}

/** Each toast's timer: it pauses under the pointer or focus and resumes with ≥1500ms. */
const toastTimers = new Map<
  number,
  { left: number; started: number; handle?: ReturnType<typeof setTimeout> }
>();
const TOAST_RESUME_MS = 1500;

function runToast(id: number): void {
  const t = toastTimers.get(id);
  if (!t) return;
  clearTimeout(t.handle);
  t.started = Date.now();
  t.handle = setTimeout(() => dismissToast(id), t.left);
}
function pauseToast(id: number): void {
  const t = toastTimers.get(id);
  if (!t || t.handle === undefined) return;
  clearTimeout(t.handle);
  t.handle = undefined;
  t.left = Math.max(TOAST_RESUME_MS, t.left - (Date.now() - t.started));
}

export function toast(text: string, o: Omit<ToastItem, 'id' | 'text'> = {}): () => void {
  const id = ++seq;
  // at most 3; the oldest goes first
  const drop = useOverlays
    .getState()
    .toasts.filter((x) => !x.closing)
    .slice(0, -2);
  for (const x of drop) toastTimers.delete(x.id);
  useOverlays.setState((s) => ({
    toasts: [...s.toasts.filter((x) => !drop.includes(x)), { id, text, ...o }],
  }));
  toastTimers.set(id, { left: o.duration ?? (o.action ? 7000 : 3600), started: Date.now() });
  runToast(id);
  return () => dismissToast(id);
}

export function dismissToast(id: number): void {
  const t = toastTimers.get(id);
  if (t) clearTimeout(t.handle);
  toastTimers.delete(id);
  if (!useOverlays.getState().toasts.some((x) => x.id === id)) return;
  useOverlays.setState((s) => ({
    toasts: s.toasts.map((x) => (x.id === id ? { ...x, closing: true } : x)),
  }));
  setTimeout(
    () => useOverlays.setState((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
    150,
  );
}

/**
 * Where the toasts sit: bottom-centre of the main pane, 16 above the
 * composer (24 above the pane's foot when it has none); inside an open
 * dialog or layer, 24 above the window's foot. Whole pixels. Returns what it
 * measured, for `followFrame`.
 */
function placeToasts(el: HTMLElement): (Element | null)[] {
  const layer = document.querySelector('.nx-dlg-layer, .nx-set-layer, .ns-adm-layer');
  const pane = layer
    ? null
    : ([...document.querySelectorAll<HTMLElement>('.hub-pane')].find(
        (p) => p.offsetParent !== null && !p.classList.contains('ns-embedded'),
      ) ?? null);
  let mid = innerWidth / 2;
  let bottom = 24;
  const app = document.getElementById('nx-app');
  const foot = !layer && app && readLayout().layout === 'phone' ? paneFoot(app) : null;
  let cmp: HTMLElement | null = null;
  if (foot) {
    bottom = innerHeight - foot.getBoundingClientRect().top + 8;
  } else if (pane) {
    const r = pane.getBoundingClientRect();
    mid = r.left + r.width / 2;
    // over the conversation column: centred on the composer when there is one
    cmp = pane.querySelector<HTMLElement>('.cmp-wrap, .cmp');
    let c = cmp?.getBoundingClientRect();
    if (c && c.width > 0) mid = c.left + c.width / 2;
    else {
      // a voice room (or a call) without its chat: above its control bar,
      // never over its buttons (QA V-05)
      cmp = pane.querySelector<HTMLElement>('.hubv-barwrap');
      c = cmp?.getBoundingClientRect();
      if (!c || !c.width) c = undefined;
    }
    bottom = innerHeight - (c ? c.top : r.bottom - 8) + 16;
  }
  // one width for the stack (the widest toast), and every edge on a 4px grid
  // so the glass corners land on whole device pixels (§2.4)
  delete el.dataset.placed;
  el.style.width = '';
  let widest = 0;
  for (const t of el.querySelectorAll<HTMLElement>('.nx-toast'))
    widest = Math.max(widest, t.getBoundingClientRect().width);
  const w = Math.min(el.getBoundingClientRect().width, Math.ceil(widest / 4 - 1e-3) * 4);
  el.style.width = w + 'px';
  const x = Math.max(16, Math.min(innerWidth - 16 - w, mid - w / 2));
  el.style.left = Math.floor(x / 4) * 4 + 'px';
  const edge = Math.floor((innerHeight - bottom) / 4) * 4;
  el.style.bottom = innerHeight - edge + 'px';
  el.dataset.placed = '';
  // the pane too: a voice bar showing under it moves the composer up
  return foot ? [foot, foot.closest('.hub-pane')] : [pane, cmp];
}

/** The conversation, the room, Friends: the main pane's own pane. */
const MAIN_PANE = '.nx-main > .hub-pane:not(.ns-embedded)';

/**
 * Off the desktop layout only one pane shows at a time on a phone (§12.2),
 * and a tablet shows the main pane: its foot is the composer in a
 * conversation, a voice room's control bar, the voice bar under the pane,
 * or the pane's own end; the
 * dock on a phone's home pane. Null before the frame is laid out.
 */
function paneFoot(app: HTMLElement): HTMLElement | null {
  const at =
    app.dataset.pane === 'home'
      ? app.querySelector<HTMLElement>('.nx-dock')
      : (app.querySelector<HTMLElement>(`${MAIN_PANE} .cmp-wrap, ${MAIN_PANE} .cmp`) ??
        // a voice room without its chat: its control bar (QA V-05)
        app.querySelector<HTMLElement>(`${MAIN_PANE} .hubv-barwrap`) ??
        app.querySelector<HTMLElement>('.nx-main .nx-vbar') ??
        app.querySelector<HTMLElement>('.nx-main'));
  return at && at.getBoundingClientRect().height ? at : null;
}

/** The shown pane's header: a phone's home pane has the server's (or the rail's Jump). */
function paneHead(app: HTMLElement): HTMLElement | null {
  return app.dataset.pane === 'home'
    ? (app.querySelector<HTMLElement>('.nx-col .srv-head') ??
        app.querySelector<HTMLElement>('.nx-col .rail-btn.jump'))
    : app.querySelector<HTMLElement>(`${MAIN_PANE} > .ch-head`);
}

export interface PaneBand {
  /** Window px: the first row under the header, and the row the foot starts on. */
  top: number;
  bottom: number;
  /** What was measured, for `followFrame`. */
  measured: (Element | null)[];
}

/**
 * The free band of the shown pane on a phone or a tablet (§12.2), for
 * something that floats over it and must leave the pane's own controls
 * reachable (the poke stack): under the header (Back, the title, the
 * actions) and above the foot (`paneFoot`), inside the safe area. A phone's
 * full-screen Settings layer: under its bar and above the home indicator, or
 * its unsaved bar. Null on the desktop layout.
 */
export function paneBand(): PaneBand | null {
  if (readLayout().layout === 'desktop') return null;
  const app = document.getElementById('nx-app');
  if (!app) return null;
  const set =
    readLayout().layout === 'phone'
      ? document.querySelector<HTMLElement>('#nx-portal > .nx-set-layer')
      : null;
  if (set) {
    const bar = [...set.querySelectorAll<HTMLElement>('.nx-set-bar')].find(
      (b) => b.getBoundingClientRect().height > 0,
    );
    const save = set.querySelector<HTMLElement>('.ns-savebar');
    const r = set.getBoundingClientRect();
    const s = save?.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(app).paddingBottom) || 0;
    return {
      top: bar ? bar.getBoundingClientRect().bottom : r.top,
      bottom: s && s.height ? s.top : r.bottom - pad,
      measured: [set, bar ?? null, save ?? null],
    };
  }
  const head = paneHead(app);
  const foot = paneFoot(app);
  const box = app.getBoundingClientRect();
  const cs = getComputedStyle(app);
  const h = head?.getBoundingClientRect();
  return {
    top: h && h.height ? h.bottom : box.top + (parseFloat(cs.paddingTop) || 0),
    bottom: foot
      ? foot === app.querySelector('.nx-main')
        ? foot.getBoundingClientRect().bottom
        : foot.getBoundingClientRect().top
      : box.bottom - (parseFloat(cs.paddingBottom) || 0),
    measured: [head, foot, foot?.closest('.hub-pane') ?? null, app.querySelector('.nx-main')],
  };
}

/**
 * Keeps something that floats over the frame placed (toasts, the poke
 * stack): runs `place` now, then again (once a frame at most) whenever what
 * it measured may have moved: a dialog or a full-screen layer opening or
 * closing in the portal, a phone's pane changing, the window or the visual
 * viewport (the on-screen keyboard) resizing, or an element `place` returns
 * changing size (a composer growing, a voice bar showing under the pane).
 * Returns the stop.
 */
export function followFrame(place: () => readonly (Element | null | undefined)[]): () => void {
  const vv = (globalThis as { visualViewport?: VisualViewport }).visualViewport;
  let frame = 0;
  let watched: Element[] = [];
  let app: HTMLElement | null = null;
  const again = () => {
    if (!frame) frame = requestAnimationFrame(run);
  };
  const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(again);
  const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(again);
  function run() {
    frame = 0;
    const els = place().filter((e): e is Element => !!e);
    if (ro && (els.length !== watched.length || els.some((e, i) => e !== watched[i]))) {
      ro.disconnect();
      for (const e of els) ro.observe(e);
      watched = els;
    }
    const a = document.getElementById('nx-app');
    if (mo && a && a !== app) {
      app = a;
      mo.observe(a, { attributes: true, attributeFilter: ['data-pane', 'data-layout'] });
    }
  }
  mo?.observe(portalRoot(), { childList: true });
  addEventListener('resize', again);
  vv?.addEventListener('resize', again);
  run();
  return () => {
    cancelAnimationFrame(frame);
    ro?.disconnect();
    mo?.disconnect();
    removeEventListener('resize', again);
    vv?.removeEventListener('resize', again);
  };
}

/**
 * Toasts are the app's own sentences, so they are laid out in the UI
 * language's direction, never guessed from the text: `dir="auto"` took it
 * from the first letter, so "{name} is blocked." with a right-to-left name came
 * out right to left (".is blocked کوروش"). Names inside are isolated by
 * the translator (FSI…PDI, i18n/core.ts). They never take a click from what
 * is under them; only their own buttons do (overlays.css). The pointer over
 * one (followed, since the toast itself lets clicks through) or the focus in
 * it pauses its timer.
 */
function Toasts() {
  const toasts = useOverlays((s) => s.toasts);
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);
  // placed now, and again while they show whenever the frame under them
  // moves: a Settings layer closing must not leave them over the dock (QA-03)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !toasts.length) return;
    return followFrame(() => placeToasts(el));
  }, [toasts]);
  // hover: the toasts let the pointer through, so follow it
  useEffect(() => {
    if (!toasts.length) return;
    let over = new Set<number>();
    const move = (e: PointerEvent) => {
      const now = new Set<number>();
      for (const n of ref.current?.querySelectorAll<HTMLElement>('.nx-toast') ?? []) {
        const r = n.getBoundingClientRect();
        if (
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom
        )
          now.add(Number(n.dataset.id));
      }
      for (const id of now) if (!over.has(id)) pauseToast(id);
      for (const id of over) if (!now.has(id) && !focused.has(id)) runToast(id);
      over = now;
    };
    const focused = new Set<number>();
    const fin = (e: FocusEvent) => {
      const n = (e.target as Element | null)?.closest?.<HTMLElement>('.nx-toast');
      if (!n) return;
      focused.add(Number(n.dataset.id));
      pauseToast(Number(n.dataset.id));
    };
    const fout = (e: FocusEvent) => {
      const n = (e.target as Element | null)?.closest?.<HTMLElement>('.nx-toast');
      if (!n) return;
      const id = Number(n.dataset.id);
      focused.delete(id);
      if (!over.has(id)) runToast(id);
    };
    addEventListener('pointermove', move, { passive: true });
    document.addEventListener('focusin', fin);
    document.addEventListener('focusout', fout);
    return () => {
      removeEventListener('pointermove', move);
      document.removeEventListener('focusin', fin);
      document.removeEventListener('focusout', fout);
    };
  }, [toasts.length]);
  return (
    <div className="nx-toasts" ref={ref}>
      {toasts.map((x) => (
        <div
          key={x.id}
          data-id={x.id}
          className="nx-toast nx-glass strong"
          role={x.tone === 'danger' ? 'alert' : 'status'}
          dir="ltr"
          data-tone={x.tone}
          data-state={x.closing ? 'closed' : undefined}
        >
          {x.icon ? (
            <span className="nx-toast-ic">
              <Icon name={x.icon} />
            </span>
          ) : null}
          <span className="nx-toast-t">
            <span>{x.text}</span>
          </span>
          {x.action ? (
            <Btn
              kind="quiet"
              size="xs"
              className="nx-toast-a"
              onClick={() => {
                dismissToast(x.id);
                x.action?.run();
              }}
            >
              {x.action.label}
            </Btn>
          ) : null}
          <button
            type="button"
            className="nx-toast-x"
            aria-label={t('c.close')}
            onClick={() => dismissToast(x.id)}
          >
            <Icon name="x" />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ---------- tooltips (data-tip, visual only; §7.19) ---------- */

/** 300ms cold, at once within 600ms of the last one, 100ms on the server rail. */
const TIP_COLD_MS = 300;
const TIP_WARM_MS = 600;
const TIP_RAIL_MS = 100;

/**
 * The tooltip bubble. While it shows, it follows its element: a new
 * `data-tip` (or `data-tip-pos`) is shown at once, and it hides when the
 * element loses its `data-tip` or leaves the page. QA 2026-10-28 follow-up
 * round 3: hovering your own mic, a server deafen arrived and the bubble
 * still said "Mute" (the text was copied once, when it opened). It points
 * at its element with a caret, and fades out over 80ms.
 */
function Tooltip() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let fade: ReturnType<typeof setTimeout> | undefined;
    let current: HTMLElement | null = null;
    let last = 0;
    // the shown element: its attributes, and whether it is still on the page
    let watch: MutationObserver | null = null;
    const unwatch = () => {
      watch?.disconnect();
      watch = null;
    };
    const onRail = (target: HTMLElement) => !!target.closest('.hub-rail');
    /** Shows `target`'s words, placed beside it (measured once visible); false when it has none. */
    const render = (target: HTMLElement): boolean => {
      const txt = target.getAttribute('data-tip');
      if (!txt) return false;
      clearTimeout(fade);
      if (el.textContent !== txt) el.textContent = txt;
      el.hidden = false;
      el.dataset.state = 'open';
      const pos = (target.getAttribute('data-tip-pos') as Placement) || 'top';
      place(el, target, pos, onRail(target) ? 12 : 8);
      // the caret points at the anchor's middle, wherever the bubble was clamped
      const a = target.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      const ax = Math.max(8, Math.min(b.width - 8, a.left + a.width / 2 - b.left));
      const ay = Math.max(8, Math.min(b.height - 8, a.top + a.height / 2 - b.top));
      el.style.setProperty('--tip-ax', Math.round(ax) + 'px');
      el.style.setProperty('--tip-ay', Math.round(ay) + 'px');
      return true;
    };
    const show = (target: HTMLElement) => {
      unwatch();
      if (!render(target)) {
        hide();
        return;
      }
      current = target;
      watch = new MutationObserver(() => {
        const tg = current;
        if (!tg) return;
        if (!tg.isConnected || !render(tg)) hide();
      });
      watch.observe(target, { attributes: true, attributeFilter: ['data-tip', 'data-tip-pos'] });
      // leaving the page is a change in some ancestor's children
      watch.observe(document.body, { childList: true, subtree: true });
    };
    const hide = () => {
      clearTimeout(timer);
      unwatch();
      if (!el.hidden && el.dataset.state === 'open') {
        last = Date.now();
        if (animatesExits()) {
          el.dataset.state = 'closed';
          clearTimeout(fade);
          fade = setTimeout(() => {
            if (el.dataset.state === 'closed') el.hidden = true;
          }, 80);
        } else el.hidden = true;
      }
      current = null;
    };
    const over = (e: Event) => {
      // a finger has no hover: a tap never leaves a tooltip behind (§12)
      if ((e as PointerEvent).pointerType === 'touch') return;
      const tg = (e.target as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null;
      if (!tg || tg === current) return;
      clearTimeout(timer);
      const warm = Date.now() - last < TIP_WARM_MS || (current !== null && !el.hidden);
      timer = setTimeout(() => show(tg), warm ? 0 : onRail(tg) ? TIP_RAIL_MS : TIP_COLD_MS);
    };
    const out = (e: Event) => {
      const tg = (e.target as HTMLElement | null)?.closest?.('[data-tip]');
      if (tg) hide();
    };
    const focusIn = (e: Event) => {
      const tg = (e.target as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null;
      if (tg && (tg as HTMLElement).matches(':focus-visible')) show(tg);
    };
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerout', out);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('focusin', focusIn);
    document.addEventListener('focusout', hide);
    document.addEventListener('keydown', hide, true);
    return () => {
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('focusin', focusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('keydown', hide, true);
      clearTimeout(timer);
      clearTimeout(fade);
      unwatch();
    };
  }, []);
  return <div ref={ref} className="nx-tip" role="tooltip" hidden />;
}

/* ---------- dialogs (§7.18) ---------- */

export interface DialogProps {
  title?: string;
  label?: string;
  icon?: IconName;
  tone?: 'mode' | 'danger';
  cls?: string;
  /** sm 440 (default), md 560, lg 720. */
  size?: 'sm' | 'md' | 'lg';
  /** Older callers: a width maps to the nearest size (≤500 sm, ≤640 md, else lg). */
  width?: number;
  onClose: () => void;
  dismissable?: boolean;
  children: ReactNode;
  actions?: ReactNode;
  stack?: boolean;
  wrapCls?: string;
  initialFocus?: RefObject<HTMLElement | null>;
}

const snap4 = (v: number, up: boolean) =>
  (up ? Math.ceil(v / 4 - 1e-3) : Math.floor(v / 4 + 1e-3)) * 4;

/**
 * Keeps a dialog on a 4px grid: its height is padded up to a multiple of 4
 * (WP0's ban dialog was 469.75 tall from its content's line heights) and its
 * top-left corner sits on a multiple of 4, so every corner lands on whole
 * device pixels at 100, 125, 150 and 200% (§2.4). Re-run when its content
 * or the window changes size.
 */
function useSnap(ref: RefObject<HTMLDivElement | null>): void {
  useLayoutEffect(() => snapWatch(ref.current), [ref]);
}

/** Snaps the dialog now and whenever it or its wrap changes size; returns the stop. */
function snapWatch(el: HTMLDivElement | null): (() => void) | undefined {
  {
    const wrap = el?.parentElement;
    if (!el || !wrap || typeof ResizeObserver === 'undefined') return;
    let pad = 0;
    const run = () => {
      const w = wrap.getBoundingClientRect();
      // layout sizes (the opening scale must not count), with their fractions
      const cs = getComputedStyle(el);
      const r = { width: parseFloat(cs.width) || 0, height: parseFloat(cs.height) || 0 };
      // the wrap's own padding (24, the palette's top at 15vh) is dropped
      // once snapped, so it is restated here, not read
      const top15 = wrap.classList.contains('top');
      const pt = top15 ? Math.max(24, innerHeight * 0.15) : 24;
      const pb = 24;
      const pl = 24;
      const pr = 24;
      const natural = r.height - pad;
      const h = snap4(natural, true);
      const next = Math.max(0, Math.round((h - natural) * 1000) / 1000);
      if (Math.abs(next - pad) > 1e-3) {
        pad = next;
        el.style.setProperty('--dlg-pad', pad + 'px');
      }
      const top = wrap.classList.contains('top')
        ? w.top + pt
        : w.top + pt + Math.max(0, (w.height - pt - pb - h) / 2);
      const left = w.left + pl + Math.max(0, (w.width - pl - pr - r.width) / 2);
      el.style.setProperty('--dlg-y', Math.max(0, snap4(top, false) - w.top) + 'px');
      el.style.setProperty('--dlg-x', Math.max(0, snap4(left, false) - w.left) + 'px');
      wrap.dataset.snap = '';
    };
    run();
    const ro = new ResizeObserver(run);
    ro.observe(el);
    ro.observe(wrap);
    return () => ro.disconnect();
  }
}

/** A dialog's scroll state: a line under the header once scrolled, over the actions while more is below. */
function scrollMarks(body: HTMLElement, dlg: HTMLElement): void {
  const more = body.scrollHeight - body.clientHeight - body.scrollTop > 1;
  dlg.toggleAttribute('data-top', body.scrollTop > 0);
  dlg.toggleAttribute('data-more', more);
}

/**
 * The exit (I13): a dialog with a title leaves a copy that fades and shrinks
 * for 120ms. The copy is not the dialog: no `.nx-dlg-layer`, no `.nx-dlg`,
 * no ids, names or roles, so nothing can find or use it.
 */
function ghostOut(layer: HTMLElement | null): void {
  if (!layer || !layer.isConnected || !animatesExits() || layer.hasAttribute('data-instant'))
    return;
  const g = layer.cloneNode(true) as HTMLElement;
  g.className = 'nx-dlg-ghost';
  g.setAttribute('aria-hidden', 'true');
  g.inert = true;
  g.dataset.state = 'closed';
  const box = g.querySelector<HTMLElement>('.nx-dlg');
  if (box) {
    box.classList.remove('nx-dlg');
    box.classList.add('nx-dlg-out');
  }
  for (const n of [g, ...g.querySelectorAll<HTMLElement>('*')]) {
    for (const a of [
      'id',
      'name',
      'role',
      'autocomplete',
      'for',
      'data-autofocus',
      'aria-labelledby',
    ])
      n.removeAttribute(a);
  }
  document.body.appendChild(g);
  afterExit(box ?? g, () => g.remove(), 200);
}

/** A modal dialog rendered into the portal (prototype NX.ui.dialog). */
export function Dialog({
  title,
  label,
  icon,
  tone,
  cls,
  size,
  width,
  onClose,
  dismissable = true,
  children,
  actions,
  stack,
  wrapCls,
  initialFocus,
}: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const restore = useRef<HTMLElement | null>(null);
  const [titleId] = useState(() => `dlg-${++seq}`);
  const closeRef = useRef(onClose);
  const sz =
    size ?? (width === undefined ? 'sm' : width <= 500 ? 'sm' : width <= 640 ? 'md' : 'lg');
  useEffect(() => {
    closeRef.current = onClose;
  });
  useSnap(ref);
  // the exit runs on a copy (the dialog itself is gone with its parent)
  useLayoutEffect(() => {
    const el = layer.current;
    return () => {
      if (title) ghostOut(el);
    };
  }, [title]);
  useEffect(() => {
    const b = body.current;
    const d = ref.current;
    if (!b || !d || typeof ResizeObserver === 'undefined') return;
    const mark = () => scrollMarks(b, d);
    mark();
    b.addEventListener('scroll', mark, { passive: true });
    const ro = new ResizeObserver(mark);
    ro.observe(b);
    for (const c of b.children) ro.observe(c);
    return () => {
      b.removeEventListener('scroll', mark);
      ro.disconnect();
    };
  }, []);
  useEffect(() => {
    restore.current = document.activeElement as HTMLElement | null;
    const byPointer = modality === 'pointer';
    const off = pushEscape(() => {
      if (dismissable) closeRef.current();
    });
    // phones and tablets: Back closes it too
    const offBack = dismissable && backStackOn() ? pushBack(() => closeRef.current()) : null;
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      // [data-autofocus], then the first text field, then the dialog itself;
      // never the primary or a destructive button (§7.18)
      const f =
        initialFocus?.current ??
        el.querySelector<HTMLElement>('[data-autofocus]') ??
        el.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([disabled]), textarea:not([disabled])',
        ) ??
        el;
      f.focus({
        preventScroll: true,
        ...(byPointer ? { focusVisible: false } : {}),
      } as FocusOptions);
    });
    return () => {
      off();
      offBack?.();
      const r = restore.current;
      if (r && document.contains(r)) r.focus({ preventScroll: true });
    };
  }, [dismissable, initialFocus]);
  return createPortal(
    <div className="nx-dlg-layer" data-state="open" ref={layer}>
      <div className="nx-scrim" onClick={() => dismissable && onClose()} />
      <div
        className={'nx-dlgwrap' + (wrapCls ? ' ' + wrapCls : '')}
        onClick={(e) => {
          if (e.target === e.currentTarget && dismissable) onClose();
        }}
      >
        <div
          ref={ref}
          className={'nx-dlg nx-raised' + (title ? ' has-h' : '') + (cls ? ' ' + cls : '')}
          data-size={sz}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          {...(title ? { 'aria-labelledby': titleId } : { 'aria-label': label })}
          onKeyDown={(e) => {
            if (e.key !== 'Tab') return;
            const list = focusables(e.currentTarget);
            if (!list.length) return;
            const first = list[0]!;
            const lastEl = list[list.length - 1]!;
            if (
              e.shiftKey &&
              (document.activeElement === first || document.activeElement === e.currentTarget)
            ) {
              e.preventDefault();
              lastEl.focus();
            } else if (!e.shiftKey && document.activeElement === lastEl) {
              e.preventDefault();
              first.focus();
            }
          }}
        >
          {title ? (
            <div className="nx-dlg-h">
              {icon ? (
                <div className="nx-dlg-ic" data-tone={tone}>
                  <Icon name={icon} />
                </div>
              ) : null}
              <h2 id={titleId}>{title}</h2>
              {dismissable ? (
                <button
                  type="button"
                  className="nx-dlg-x"
                  aria-label={tr().t('c.close')}
                  data-tip={tr().t('c.close')}
                  onClick={() => onClose()}
                >
                  <Icon name="x" />
                </button>
              ) : null}
            </div>
          ) : icon ? (
            <div className="nx-dlg-ic" data-tone={tone}>
              <Icon name={icon} />
            </div>
          ) : null}
          <div className="nx-dlg-body nx-scroll-read" ref={body}>
            {children}
          </div>
          {actions ? <div className={'nx-actions' + (stack ? ' stack' : '')}>{actions}</div> : null}
          <div className="nx-dlg-snap" aria-hidden="true" />
        </div>
      </div>
    </div>,
    portalRoot(),
  );
}

/* ---------- imperative confirm / prompt ---------- */

interface AskState {
  kind: 'confirm' | 'prompt';
  title: string;
  body?: string;
  icon?: IconName;
  danger?: boolean;
  confirmLabel?: string;
  inputLabel?: string;
  inputType?: 'text' | 'password';
  error?: string;
  resolve: (v: string | boolean | null) => void;
}

const useAsk = create<{ ask: AskState | null }>(() => ({ ask: null }));

export function confirmDialog(o: {
  title: string;
  body?: string;
  confirmLabel?: string;
  danger?: boolean;
  icon?: IconName;
}): Promise<boolean> {
  return new Promise((resolve) => {
    useAsk.setState({ ask: { kind: 'confirm', ...o, resolve: (v) => resolve(v === true) } });
  });
}

export function promptDialog(o: {
  title: string;
  body?: string;
  inputLabel: string;
  inputType?: 'text' | 'password';
  confirmLabel?: string;
  icon?: IconName;
  error?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    useAsk.setState({
      ask: { kind: 'prompt', ...o, resolve: (v) => resolve(typeof v === 'string' ? v : null) },
    });
  });
}

function AskDialog() {
  const ask = useAsk((s) => s.ask);
  const [value, setValue] = useState('');
  const { t } = useT();
  if (!ask) return null;
  const done = (v: string | boolean | null) => {
    useAsk.setState({ ask: null });
    setValue('');
    ask.resolve(v);
  };
  return (
    <Dialog
      title={ask.title}
      icon={ask.icon ?? (ask.danger ? 'alert' : undefined)}
      tone={ask.danger ? 'danger' : 'mode'}
      onClose={() => done(ask.kind === 'confirm' ? false : null)}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={() => done(ask.kind === 'confirm' ? false : null)}>
            {t('c.cancel')}
          </Btn>
          <Btn
            kind={ask.danger ? 'danger' : 'primary'}
            size="sm"
            onClick={() => {
              // never disabled for an empty field: checked on submit (§7)
              if (ask.kind === 'prompt' && !value) {
                document.querySelector<HTMLInputElement>('.nx-dlg [data-autofocus]')?.focus();
                return;
              }
              done(ask.kind === 'confirm' ? true : value);
            }}
          >
            {ask.confirmLabel ?? t('c.done')}
          </Btn>
        </>
      }
    >
      {ask.body ? <p>{ask.body}</p> : null}
      {ask.kind === 'prompt' ? (
        <form
          className="ns-field"
          onSubmit={(e) => {
            e.preventDefault();
            if (value) done(value);
          }}
        >
          <label>
            <span className="ns-label">{ask.inputLabel}</span>
            <input
              className="nx-input"
              type={ask.inputType ?? 'text'}
              dir="auto"
              value={value}
              data-autofocus
              autoComplete="off"
              onChange={(e) => setValue(e.target.value)}
              aria-invalid={!!ask.error || undefined}
            />
          </label>
          {ask.error ? (
            <p className="ns-err" role="alert">
              {ask.error}
            </p>
          ) : null}
        </form>
      ) : null}
    </Dialog>
  );
}

/* ---------- the host ---------- */

export function OverlayHost() {
  const pops = useOverlays((s) => s.pops);
  const leaving = useOverlays((s) => s.leaving);
  // outside clicks close the top popover; a submenu's, outside its menu
  // too, closes that menu as well (a click inside the menu keeps it)
  useEffect(() => {
    const down = (e: PointerEvent) => {
      const pops = useOverlays.getState().pops;
      const target = e.target as Node;
      // a dialog above it handles its own clicks (the image viewer too: a profile
      // card's picture opens over the card, which stays when the picture closes)
      if ((target as Element).closest?.('.nx-dlg-layer, .lb')) return;
      if ((target as Element).classList?.contains('nx-menu-scrim')) swallowNextClick();
      let p = pops.at(-1);
      while (p) {
        if (document.querySelector(`[data-pop="${p.id}"]`)?.contains(target)) return;
        // a press on its own opener: the click that follows must not open it again
        closePopover(p.id, p.anchor instanceof Element && p.anchor.contains(target));
        const up: number | undefined = p.parent;
        p = up === undefined ? undefined : pops.find((x) => x.id === up);
      }
    };
    document.addEventListener('pointerdown', down, true);
    return () => document.removeEventListener('pointerdown', down, true);
  }, []);
  return createPortal(
    <>
      {/* one list (a closing pop-up keeps its node and state), the open ones
          first so a query finds the live pop-up before a fading one */}
      {[...pops, ...(leaving ?? [])].map((p) => (
        <PopLayer key={p.id} item={p} leaving={!pops.includes(p)} />
      ))}
      <Toasts />
      <AskDialog />
      <Tooltip />
    </>,
    portalRoot(),
  );
}

/** A polite live region for settled state changes. */
export function announce(text: string): void {
  const el = document.getElementById('nx-live');
  if (!el) return;
  el.textContent = '';
  setTimeout(() => {
    el.textContent = text;
  }, 30);
}

export { tr };
